import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Phone, Menu, X, MessageCircle } from "lucide-react";
import { Logo } from "@/components/Logo";
import { company } from "@/config/content";
import { useLang } from "@/i18n/LanguageContext";
import { openWhatsApp, emergencyMessage } from "@/lib/whatsapp";
import { CallButton, WhatsAppButton, LocationButton } from "@/components/ActionButtons";

function LangSwitch({ className = "" }) {
  const { lang, setLang } = useLang();
  return (
    <div className={`inline-flex items-center rounded-md border border-edge p-0.5 ${className}`} data-testid="lang-switch">
      {["it", "en"].map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => setLang(l)}
          data-testid={`lang-${l}`}
          aria-pressed={lang === l}
          className={`rounded px-2.5 py-1 text-xs font-bold uppercase transition-colors ${
            lang === l ? "bg-amber text-carbon" : "text-steel hover:text-ice"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { t } = useLang();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const navItems = [
    { to: "/", label: t.nav.home },
    { to: "/servizi", label: t.nav.services },
    { to: "/azienda", label: t.nav.company },
    { to: "/contatti", label: t.nav.contact },
  ];

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          scrolled ? "border-b border-edge bg-carbon/85 backdrop-blur-md" : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3.5 lg:px-8">
          <Logo />

          <nav className="hidden items-center gap-8 lg:flex" aria-label="Primaria">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.to === "/" ? "home" : item.to.slice(1)}`}
                className={`text-sm font-medium transition-colors hover:text-amber ${
                  location.pathname === item.to ? "text-amber" : "text-ice"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            {company.isOpen24 && (
              <span className="inline-flex items-center gap-2 text-xs font-semibold text-steel" data-testid="operative-indicator">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber opacity-70" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-amber" />
                </span>
                {t.common.operativeNow}
              </span>
            )}
            <LangSwitch />
            <button
              type="button"
              onClick={() => openWhatsApp(emergencyMessage(t.lang))}
              data-testid="header-whatsapp-icon"
              aria-label="WhatsApp"
              className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-edge text-whatsapp transition-colors hover:bg-whatsapp/10 active:scale-95"
            >
              <MessageCircle className="h-5 w-5" />
            </button>
            <CallButton size="sm" testId="header-call-btn" />
          </div>

          <div className="flex items-center gap-2 lg:hidden">
            <LangSwitch />
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
              aria-expanded={open}
              className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-edge text-ice active:scale-95"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 bg-carbon/95 backdrop-blur-lg lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex h-full flex-col justify-between px-6 pb-10 pt-24">
              <nav className="flex flex-col gap-2" aria-label="Mobile">
                {navItems.map((item, i) => (
                  <motion.div
                    key={item.to}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 + i * 0.06 }}
                  >
                    <Link
                      to={item.to}
                      data-testid={`mobile-nav-${item.to === "/" ? "home" : item.to.slice(1)}`}
                      className="block border-b border-edge py-4 font-display text-2xl font-bold text-ice"
                    >
                      {item.label}
                    </Link>
                  </motion.div>
                ))}
              </nav>
              <div className="grid gap-3">
                <CallButton full size="lg" testId="mobile-menu-call" />
                <WhatsAppButton full testId="mobile-menu-whatsapp" />
                <LocationButton full testId="mobile-menu-location" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
